// src/core/terminal.ts

import { Terminal, ITerminalOptions, ITheme } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebglAddon } from '@xterm/addon-webgl';
import { SearchAddon, ISearchOptions } from '@xterm/addon-search';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Unicode11Addon } from '@xterm/addon-unicode11';
import { SerializeAddon } from '@xterm/addon-serialize';

export const DEFAULT_TERMINAL_OPTIONS: ITerminalOptions = {
  cursorBlink: true,
  cursorStyle: 'block',
  cursorWidth: 1,
  fontFamily: '"Cascadia Code", "Fira Code", "JetBrains Mono", Menlo, Monaco, "Courier New", monospace',
  fontSize: 14,
  fontWeight: 'normal',
  fontWeightBold: 'bold',
  letterSpacing: 0,
  lineHeight: 1.2,
  scrollback: 10000,
  scrollSensitivity: 1,
  fastScrollSensitivity: 5,
  fastScrollModifier: 'alt',
  allowProposedApi: true,
  allowTransparency: false,
  convertEol: false,
  disableStdin: false,
  screenReaderMode: false,
  smoothScrollDuration: 0,
  macOptionIsMeta: false,
  macOptionClickForcesSelection: false,
  theme: {
    background: '#1e1e1e',
    foreground: '#d4d4d4',
    cursor: '#d4d4d4',
    cursorAccent: '#1e1e1e',
    selectionBackground: '#264f78',
    selectionForeground: '#ffffff',
    selectionInactiveBackground: '#3a3d41',
    black: '#000000',
    red: '#cd3131',
    green: '#0dbc79',
    yellow: '#e5e510',
    blue: '#2472c8',
    magenta: '#bc3fbc',
    cyan: '#11a8cd',
    white: '#e5e5e5',
    brightBlack: '#666666',
    brightRed: '#f14c4c',
    brightGreen: '#23d18b',
    brightYellow: '#f5f543',
    brightBlue: '#3b8eea',
    brightMagenta: '#d670d6',
    brightCyan: '#29b8db',
    brightWhite: '#ffffff',
  },
};

export class TerminalCore {
  private terminal: Terminal;
  private fitAddon: FitAddon;
  private webglAddon: WebglAddon | null = null;
  private searchAddon: SearchAddon;
  private webLinksAddon: WebLinksAddon;
  private unicode11Addon: Unicode11Addon;
  private serializeAddon: SerializeAddon;
  
  private _container: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private linkClickHandler: ((uri: string) => void) | null = null;
  
  constructor(options: Partial<ITerminalOptions> = {}) {
    this.terminal = new Terminal({
      ...DEFAULT_TERMINAL_OPTIONS,
      ...options,
    });
    
    this.fitAddon = new FitAddon();
    this.searchAddon = new SearchAddon();
    this.webLinksAddon = new WebLinksAddon(this.handleLinkClick.bind(this));
    this.unicode11Addon = new Unicode11Addon();
    this.serializeAddon = new SerializeAddon();
    
    this.terminal.loadAddon(this.fitAddon);
    this.terminal.loadAddon(this.searchAddon);
    this.terminal.loadAddon(this.webLinksAddon);
    this.terminal.loadAddon(this.unicode11Addon);
    this.terminal.loadAddon(this.serializeAddon);
    
    this.terminal.unicode.activeVersion = '11';
  }
  
  private _lastContainerWidth = 0;
  private _lastContainerHeight = 0;

  mount(container: HTMLElement): void {
    this._container = container;
    this.terminal.open(container);
    this.initializeRenderer();
    this.fit();
    
    this._lastContainerWidth = container.clientWidth;
    this._lastContainerHeight = container.clientHeight;
    
    this.resizeObserver = new ResizeObserver(() => {
      // Only refit if container dimensions actually changed
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === this._lastContainerWidth && h === this._lastContainerHeight) return;
      if (w === 0 || h === 0) return; // Container hidden/collapsed
      this._lastContainerWidth = w;
      this._lastContainerHeight = h;
      this.fit();
    });
    this.resizeObserver.observe(container);
  }
  
  get container(): HTMLElement | null {
    return this._container;
  }
  
  private initializeRenderer(): void {
    try {
      this.webglAddon = new WebglAddon();
      this.webglAddon.onContextLoss(() => {
        this.webglAddon?.dispose();
        this.webglAddon = null;
      });
      this.terminal.loadAddon(this.webglAddon);
    } catch (e) {
      console.warn('WebGL not available:', e);
    }
  }
  
  fit(): { rows: number; cols: number } {
    this.fitAddon.fit();
    return { rows: this.terminal.rows, cols: this.terminal.cols };
  }
  
  write(data: string | Uint8Array): void {
    this.terminal.write(data);
  }
  
  clear(): void {
    this.terminal.clear();
  }
  
  focus(): void {
    this.terminal.focus();
  }
  
  blur(): void {
    this.terminal.blur();
  }
  
  getDimensions(): { rows: number; cols: number } {
    return { rows: this.terminal.rows, cols: this.terminal.cols };
  }
  
  onData(callback: (data: string) => void): void {
    this.terminal.onData(callback);
  }
  
  onBinary(callback: (data: string) => void): void {
    this.terminal.onBinary(callback);
  }
  
  onResize(callback: (size: { rows: number; cols: number }) => void): void {
    this.terminal.onResize((e) => callback({ rows: e.rows, cols: e.cols }));
  }
  
  search(term: string, options?: ISearchOptions): boolean {
    return this.searchAddon.findNext(term, options);
  }
  
  searchPrevious(term: string, options?: ISearchOptions): boolean {
    return this.searchAddon.findPrevious(term, options);
  }
  
  clearSearch(): void {
    this.searchAddon.clearDecorations();
  }
  
  getSelection(): string {
    return this.terminal.getSelection();
  }
  
  selectAll(): void {
    this.terminal.selectAll();
  }
  
  clearSelection(): void {
    this.terminal.clearSelection();
  }
  
  serialize(): string {
    return this.serializeAddon.serialize();
  }
  
  setTheme(theme: ITheme): void {
    this.terminal.options.theme = theme;
  }
  
  setFontSize(size: number): void {
    this.terminal.options.fontSize = Math.max(8, Math.min(32, size));
    this.fit();
  }
  
  setFontFamily(family: string): void {
    this.terminal.options.fontFamily = family;
    this.fit();
  }
  
  setCursorStyle(style: 'block' | 'underline' | 'bar'): void {
    this.terminal.options.cursorStyle = style;
  }
  
  setCursorBlink(blink: boolean): void {
    this.terminal.options.cursorBlink = blink;
  }
  
  onLinkClick(handler: (uri: string) => void): void {
    this.linkClickHandler = handler;
  }
  
  private handleLinkClick(event: MouseEvent, uri: string): void {
    if (event.ctrlKey || event.metaKey) {
      if (this.linkClickHandler) {
        this.linkClickHandler(uri);
      } else {
        window.open(uri, '_blank', 'noopener,noreferrer');
      }
    }
  }
  
  dispose(): void {
    this.resizeObserver?.disconnect();
    this.webglAddon?.dispose();
    this.terminal.dispose();
  }
}
